import React from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Menu, MenuItem } from "@progress/kendo-react-layout";
import { copyToClipboard } from "../../Helpers/miscellaneous";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import { useTranslation } from "react-i18next";
import { Switch } from "@progress/kendo-react-inputs";

/**
 * Function representing an instances grid.
 * 
 * @param {Object} props - The properties object.
 * @param {Array} props.instances - The instances to be displayed in the grid.
 * @param {Function} props.onEdit - Callback function invoked when an instance is edited.
 * @param {Function} props.onDisable - Callback function invoked when an instance is disabled.
 * @param {Function} props.onEnable - Callback function invoked when an instance is enabled.
 * @param {Function} props.onDelete - Callback function invoked when an instance is deleted.
 * @returns {JSX.Element} - The JSX element representing the instances grid.
 * @see {@link InstancesGrid.test.jsx} for tests related to this component.
 */
function InstancesGrid({ instances, onCopy, onEdit, onDisable, onEnable, onDelete, onAllowEditAndPublish }) {
    const { t } = useTranslation(["Instances/Instances"]);
    const organizationUrl = usePropsStoreValue("organizationUrl");
    const projectName = usePropsStoreValue("projectName");

    const handleEdit = (_id) => onEdit(_id);
    const handleDelete = (_id) => onDelete(_id);
    const handleDisable = (_id) => onDisable(_id);
    const handleEnable = (_id) => onEnable(_id);
    const handleAllowEditAndPublish = (_id) => onAllowEditAndPublish(_id);

    const getInstanceUrl = (instanceId) =>
        `${process.env.REACT_APP_RUNTIME_URL}/${organizationUrl}/${projectName}_${instanceId.slice(0, 5)}`;

    const handleCopy = async (_id) => {
        await copyToClipboard(getInstanceUrl(_id));
        onCopy();
    };

    const handleVisit = (_id) => window.open(getInstanceUrl(_id), "_blank");

    return (
        <Grid data={instances}>
            <GridColumn
                width={40}
                title="#"
                cell={props => <td>{props.dataIndex + 1}</td>}
            />
            <GridColumn
                width={200}
                title={t("grid.name")}
                field="instanceName"
                cell={props => <td title={props.dataItem[props.field]}>{props.dataItem[props.field]}</td>}
            />
            <GridColumn
                width={125}
                title={t("grid.type")}
                cell={props => <td>{t("instance_types." + props.dataItem.instanceType.toLowerCase())}</td>}
            />
            <GridColumn
                width={125}
                title={t("grid.published")}
                cell={props => (
                    <td>
                        {props.dataItem.Published ? (
                            <span className="text-success">{t("grid.yes")}</span>
                        ) : (
                            <span className="text-danger">{t("grid.no")}</span>
                        )}
                    </td>
                )}
            />
            <GridColumn
                width={125}
                title={t("grid.status")}
                cell={props => (
                    <td>
                        {props.dataItem.isActive ? (
                            <span className="text-success">{t("grid.enabled")}</span>
                        ) : (
                            <span className="text-danger">{t("grid.disabled")}</span>
                        )}
                    </td>
                )}
            />
            <GridColumn
                title={t("grid.description")}
                cell={props => <td title={props.dataItem.description}>{props.dataItem.description}</td>}
            />
            <GridColumn
                width={250}
                title={t("grid.last_updated_on")}
                cell={props => <td>{new Date(props.dataItem.updatedDate).toLocaleString()}</td>}
            />
            <GridColumn
                width={250}
                title={t("grid.allow_edit_and_publish")}
                cell={(props) =>
                    <AllowEditAndPublish
                        instances={instances}
                        dataItem={props.dataItem}
                        handleAllowEditAndPublish={handleAllowEditAndPublish}
                    />
                }
            />
            <GridColumn
                width={80}
                title={t("grid.actions")}
                cell={props => (
                    <ActionCell
                        t={t}
                        instanceIndex={props.dataIndex}
                        instances={instances}
                        isPublished={props.dataItem.Published}
                        isActive={props.dataItem.isActive}
                        onEdit={() => handleEdit(props.dataItem._id)}
                        onVisit={() => handleVisit(props.dataItem._id)}
                        onCopy={() => handleCopy(props.dataItem._id)}
                        onDisable={() => handleDisable(props.dataItem._id)}
                        onDelete={() => handleDelete(props.dataItem._id)}
                        onEnable={() => handleEnable(props.dataItem._id)}
                    />
                )}
            />
        </Grid>
    );
}

function AllowEditAndPublish({ instances, dataItem, handleAllowEditAndPublish }) {
    const allowEditAndPublishInstance = instances.filter((instance) => instance.allowEditAndPublish).length;
    const publishedInstance = instances.filter((instance) => instance.Published).length;
    const activeInstance = instances.filter((instance) => instance.isActive).length;
    const isOnlyOneInstance = instances.length === 1;
    const disable =
        dataItem.allowEditAndPublish &&
        (publishedInstance === 1 ||
            activeInstance === 1 ||
            allowEditAndPublishInstance === 1 ||
            isOnlyOneInstance);

    return (
        <td>
            <Switch
                checked={dataItem.allowEditAndPublish}
                onChange={() => handleAllowEditAndPublish(dataItem._id)}
                disabled={disable}
            />
        </td>
    );
}

function ActionCell({ t, instanceIndex, isPublished, isActive, instances, onVisit, onEdit, onCopy, onDisable, onDelete, onEnable }) {

    let shouldDeleteBeDisabled = false;
    let shouldDisableBeDisabled = false;

    if (isActive && isPublished) {
        const otherValidInstance = instances.find((instance, index) => index !== instanceIndex && instance.isActive && instance.Published);
        if (!otherValidInstance) {
            shouldDeleteBeDisabled = true;
            shouldDisableBeDisabled = true;
        }
    }
    return (
        <td>
            <Menu onSelect={(e) => e.item.data()} className="no-dropdown-in-menu three-dots">
                <MenuItem
                    cssClass="btn btn-secondary px-1"
                    data={() => { }}
                    linkRender={props => (
                        <span aria-label="extra-options" className="k-icon k-i-more-horizontal g-0 m-0 px-3"></span>
                    )}
                >
                    <MenuItem cssClass="bg-white hover-border-0" text={t("grid.grid_actions.edit")} data={onEdit} />
                    <MenuItem cssClass="bg-white hover-border-0" text={t("grid.grid_actions.visit")} data={onVisit} disabled={!isPublished} />
                    <MenuItem cssClass="bg-white hover-border-0" text={t("grid.grid_actions.copy_address")} data={onCopy} disabled={!isPublished} />
                    {isActive ? (
                        <MenuItem cssClass="bg-white hover-border-0" text={t("grid.grid_actions.disable")} data={onDisable} disabled={shouldDisableBeDisabled} />
                    ) : (
                        <MenuItem cssClass="bg-white hover-border-0" text={t("grid.grid_actions.enable")} data={onEnable} />
                    )}
                    <MenuItem cssClass="bg-white hover-border-0" text={t("grid.grid_actions.delete")} data={onDelete} disabled={shouldDeleteBeDisabled} />
                </MenuItem>
            </Menu>
        </td>
    );
}

export default InstancesGrid;
